import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { palette } from '../palette';
import { Email, Google } from '@mui/icons-material';
import { colors } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { useLoginButtonController } from './useLoginButtonController';

interface LoginButtonProps extends PropsWithChildren {
  onLoginClicked?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  authenticationRedirectUrl?: string;
  loginButtonHelp: string | React.ReactElement;
}

export function LoginButton({ children, loginButtonHelp, onLoginClicked, disabled = false }: LoginButtonProps) {
  const {
    authUrl,
    email,
    emailError,
    magicLinkSent,
    magicLinkLoading,
    magicLinkError,
    handleEmailChange,
    handleSendMagicLink,
    handleKeyDown,
    handleTryDifferentEmail,
  } = useLoginButtonController();

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        flex: 'auto',
        alignItems: 'center',
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '15vh',
          mb: 20,
        }}
      >
        <img alt="We Hate Exercise" width={'200px'} src={'./logo.png'} />
      </Box>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        {/* Google Sign In */}
        {disabled && (
          <Tooltip title={loginButtonHelp} arrow placement={'top'}>
            <span>
              <Button disabled fullWidth variant="contained" sx={{ py: 1.25, borderRadius: 1.5 }}>
                <Google sx={{ mr: 1, fontSize: '1.2rem', color: colors.common.white }} />
                <Typography variant="body2" sx={{ color: colors.common.white }}>
                  Sign In with Google
                </Typography>
              </Button>
            </span>
          </Tooltip>
        )}
        {!disabled && (
          <Tooltip title={loginButtonHelp} arrow placement={'top'}>
            <Button
              fullWidth
              href={authUrl}
              onClick={onLoginClicked}
              variant="contained"
              sx={{
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: palette.primary.main,
                textTransform: 'none',
                '&:hover': { bgcolor: palette.primary.dark },
              }}
            >
              <Google sx={{ mr: 1, fontSize: '1.2rem', color: colors.common.white }} />
              <Typography variant="body2" sx={{ color: colors.common.white }}>
                Sign In with Google
              </Typography>
            </Button>
          </Tooltip>
        )}

        {/* Divider */}
        <Divider sx={{ my: 2.5, color: palette.primary.dark, fontSize: '0.85rem' }}>or sign in with email</Divider>

        {/* Magic Link Form */}
        {magicLinkSent ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert
              severity="success"
              variant="outlined"
              sx={{ mb: 2, borderRadius: 1.5, '& .MuiAlert-message': { width: '100%', textAlign: 'center' } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Check your email for a sign-in link.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                It expires in 15 minutes.
              </Typography>
            </Alert>
            <Button
              fullWidth
              variant="text"
              size="small"
              onClick={handleTryDifferentEmail}
              sx={{ color: palette.primary.main, textTransform: 'none' }}
            >
              Use a different email
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="you@example.com"
              label="Email address"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onKeyDown={handleKeyDown}
              error={!!emailError}
              helperText={emailError}
              disabled={magicLinkLoading}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ fontSize: '1.1rem', color: palette.secondary.main }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  backgroundColor: palette.background.paper,
                },
              }}
            />
            {magicLinkError && (
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 1.5, py: 0 }}>
                {magicLinkError}
              </Alert>
            )}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendMagicLink}
              disabled={magicLinkLoading}
              sx={{
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: palette.primary.main,
                textTransform: 'none',
                '&:hover': { bgcolor: palette.primary.dark },
              }}
            >
              {magicLinkLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                <>
                  <Email sx={{ mr: 1, fontSize: '1.2rem', color: colors.common.white }} />
                  <Typography variant="body2" sx={{ color: colors.common.white }}>
                    Send Magic Link
                  </Typography>
                </>
              )}
            </Button>
          </Box>
        )}
      </Box>
      {children}
    </Box>
  );
}
